import React, { useEffect, useState } from 'react'
import { View, ActivityIndicator, ViewStyle } from 'react-native'
import { observer } from 'mobx-react-lite'
import { IconButton } from '@mui/material'
import { SearchRounded, FilterList } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { useStores, setPageTitle, FilterTermsProps } from '@trivie/core'
import { color } from '@trivie/ui-web/theme'
import { Header, TabController } from '@trivie/ui-mobile'

import { TopicsTab } from './tabs'
import { QuizzesTab } from './tabs/quizzes-tab'
import { ResourcesTab } from './tabs/resources-tab'
import { SubscriptionsTab } from './tabs/subscriptions-tab'
import { FilterLibraryModal } from './library-filter-modal'
import { NullState } from './null-state'

export const LibraryScreen = observer(() => {
  const { libraryStore, gameStore, userStore, uiStore } = useStores()
  const { setQuiz } = gameStore
  const {
    getTopicsForUser,
    getResourcesForUser,
    getQuizzesForUser,
    getAHAContentPacksForUser,
    quizzes,
    allResources,
    topicsSansGeneral,
    contentPacks,
    isLoadingQuizzes,
    isLoadingResources,
    isLoadingTopics,
  } = libraryStore
  const { user, getBranding } = userStore

  const [activeTab, setActiveTab] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [showFilterModal, setShowFilterModal] = useState(false)

  const [filterTopicTerms, setFilterTopicTerms] = useState<FilterTermsProps>({
    status: [],
    sources: [],
  })
  const [filterQuizTerms, setFilterQuizTerms] = useState<FilterTermsProps>({
    status: [],
    sources: [],
  })
  const [filterResourceTerms, setFilterResourceTerms] = useState<FilterTermsProps>({
    status: [],
    sources: [],
  })

  const { t } = useTranslation()

  const loadData = () => {
    if (user) {
      getBranding(user.id)
      Promise.all([
        getAHAContentPacksForUser(user.id),
        getTopicsForUser(user.id),
        getQuizzesForUser(user.id),
        getResourcesForUser(user.id),
      ])
    }
  }

  useEffect(() => {
    setPageTitle(`Library - ${process.env.REACT_APP_SITE_TITLE}`)
    loadData()
  }, [])

  useEffect(() => {
    setQuiz(undefined)
  }, [])

  const tabs = [
    {
      id: 0,
      name: 'subscriptions',
      label: 'AHA Packs',
      component: <SubscriptionsTab searchTerm={searchTerm} />,
      onPress: () => setActiveTab(0),
      numeral: String(contentPacks?.length ?? 0),
      enable: true,
    },
    {
      id: 1,
      name: 'topics',
      label: 'Library.topics',
      component: (
        <TopicsTab
          clearFilters={() => setFilterTopicTerms({ status: [], sources: [] })}
          searchTerm={searchTerm}
          filterTerms={filterTopicTerms}
        />
      ),
      onPress: () => setActiveTab(1),
      numeral: String(topicsSansGeneral?.length ?? 0),
      enable: topicsSansGeneral?.length > 0,
    },
    {
      id: 2,
      name: 'quizzes',
      label: 'Library.quizzes',
      component: (
        <QuizzesTab
          clearFilters={() => setFilterQuizTerms({ status: [], sources: [] })}
          searchTerm={searchTerm}
          filterTerms={filterQuizTerms}
        />
      ),
      onPress: () => setActiveTab(2),
      numeral: String(quizzes?.length ?? 0),
      enable: quizzes?.length > 0,
    },
    {
      id: 3,
      name: 'resources',
      label: 'Library.resources',
      component: (
        <ResourcesTab
          clearFilters={() => setFilterResourceTerms({ status: [], sources: [] })}
          searchTerm={searchTerm}
          filterTerms={filterResourceTerms}
        />
      ),
      onPress: () => setActiveTab(3),
      numeral: String(allResources?.length ?? 0),
      enable: allResources?.length > 0,
    },
  ]

  const hasContent = Boolean(
    contentPacks?.length || topicsSansGeneral?.length || quizzes?.length || allResources?.length,
  )

  const loading = isLoadingQuizzes || isLoadingResources || isLoadingTopics

  return (
    <div style={ROOT}>
      <div style={HEADER_STICKY_CONTAINER}>
        <div style={HEADER_FLEX_CONTAINER}>
          <div style={HEADER_ITEM1}>
            <Header titleStyle={{ fontSize: 24, color: color.black }} headerTx="Library.header" />
          </div>
        </div>
      </div>
      {loading ? (
        <div style={LOADER_CONTAINER}>
          <ActivityIndicator style={LOADER} size="small" />
        </div>
      ) : hasContent ? (
        <>
          <div style={SEARCH_POPUP_CONTAINER}>
            <SearchRounded style={SEARCH_ICON} />
            <input
              type="text"
              placeholder={t('Common.search')}
              style={{ ...SEARCH_INPUT, fontFamily: uiStore.branding.font_family }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <IconButton
              disabled={activeTab === 0}
              aria-label="Filter"
              onClick={() => setShowFilterModal(true)}
              size="large"
            >
              <FilterList
                htmlColor={activeTab === 0 ? color.transparent : color.black}
                style={{ fontSize: 22 }}
              />
              {activeTab === 1 && filterTopicTerms.status.length > 0 ? (
                <View style={FILTER_INDICATOR} />
              ) : null}
              {activeTab === 2 && filterQuizTerms.status.length > 0 ? (
                <View style={FILTER_INDICATOR} />
              ) : null}
              {activeTab === 3 && filterResourceTerms.sources.length > 0 ? (
                <View style={FILTER_INDICATOR} />
              ) : null}
            </IconButton>
          </div>
          <div style={CONTAINER}>
            <TabController
              matchID
              containerStyle={{
                marginBottom: 20,
                justifyContent: 'flex-start',
                paddingHorizontal: 0,
              }}
              setActiveTab={setActiveTab}
              activeTab={activeTab}
              tabs={tabs}
            />
          </div>
          {activeTab === 1 && (
            <FilterLibraryModal
              setShowModal={setShowFilterModal}
              filterTerms={filterTopicTerms}
              open={showFilterModal}
              isStatusFilter
              title="Filter Topics"
              setFilterTerms={setFilterTopicTerms}
            />
          )}
          {activeTab === 2 && (
            <FilterLibraryModal
              setShowModal={setShowFilterModal}
              filterTerms={filterQuizTerms}
              open={showFilterModal}
              isStatusFilter
              title="Filter Quizzes"
              setFilterTerms={setFilterQuizTerms}
            />
          )}
          {activeTab === 3 && (
            <FilterLibraryModal
              setShowModal={setShowFilterModal}
              filterTerms={filterResourceTerms}
              open={showFilterModal}
              isStatusFilter={false}
              title="Filter Resources"
              setFilterTerms={setFilterResourceTerms}
            />
          )}
        </>
      ) : (
        <NullState />
      )}
    </div>
  )
})

/**
 * Styling
 */
const ROOT: React.CSSProperties = { height: '100%' }
const CONTAINER: React.CSSProperties = {
  height: '100%',
}
const HEADER_STICKY_CONTAINER: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: 99,
  backgroundColor: 'white',
}
const HEADER_FLEX_CONTAINER: React.CSSProperties = {
  display: 'flex',
  maxWidth: '1440px',
  justifyContent: 'space-between',
}
const HEADER_ITEM1: React.CSSProperties = {}
const SEARCH_ICON: React.CSSProperties = {
  position: 'relative',
  left: 36,
  color: color.shade30,
  fontSize: 24,
  alignSelf: 'center',
}
const SEARCH_POPUP_CONTAINER: React.CSSProperties = {
  display: 'flex',
  position: 'sticky',
  marginTop: 15,
  marginBottom: 15,
  marginLeft: -25,
}
const SEARCH_INPUT: React.CSSProperties = {
  fontSize: 17,
  padding: 8,
  paddingLeft: 40,
  marginRight: 8,
  flex: 1,
  borderRadius: 40,
  backgroundColor: 'rgb(245,245,245)',
  borderWidth: 0,
  borderColor: color.grey,
  borderTopColor: color.grey,
}
const FILTER_INDICATOR: ViewStyle = {
  height: 8,
  width: 8,
  borderRadius: 100,
  position: 'absolute',
  backgroundColor: color.blue,
  zIndex: 999,
  right: 10,
  top: 27,
}
const LOADER_CONTAINER: React.CSSProperties = {
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  position: 'relative',
}
const LOADER: ViewStyle = {
  flex: 1,
  alignSelf: 'center',
  position: 'relative',
  height: window.innerHeight / 2,
  bottom: 100,
}
